/**
 * @file: tab-bar.tsx
 *
 * The inbox's two-tab switcher (通知 / 聊天消息) with per-tab unread
 * badges (聊天消息 only while the store has chat, with 试用中 on a trial),
 * the signed-in identity label, and the contacts entry (messages
 * tab only). Pure chrome — the host owns tab state and the contacts action.
 */

import { Typography } from '@euler/app/components/typography';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { TrialBadge } from '@euler/components/entitlements/TrialBadge';
import { FontFamily } from '@euler/components/typography';
import { StoreFeatureAccess } from '@euler/functions/entitlements/storeFeatureAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { InboxTab } from '../../types';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const InboxTabBar: FC<{
  tab: InboxTab;
  chat: StoreFeatureAccess;
  feedUnread: number;
  chatUnread: number;
  onTabChange: (next: InboxTab) => void;
  onOpenContacts?: () => void;
  /**
   * Mark every feed message read. Rendered on the right edge only when
   * the notifications tab is active AND there's actually something
   * unread — keeps the chrome quiet when the inbox is already at zero.
   */
  onMarkAllFeedRead?: () => void;
}> = memo(
  ({
    tab,
    chat,
    feedUnread,
    chatUnread,
    onTabChange,
    onOpenContacts,
    onMarkAllFeedRead,
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          padding: 0 ${rpx(20)}px;
          border-bottom-width: ${StyleSheet.hairlineWidth}px;
          border-bottom-color: #ececec;
        `}
      >
        <TabButton
          label="通知"
          active={tab === 'notifications'}
          badge={feedUnread}
          trialEndsAt={null}
          onPress={() => onTabChange('notifications')}
        />
        {chat.enabled ? (
          <>
            <View
              css={`
                width: ${rpx(24)}px;
              `}
            />
            <TabButton
              label="聊天消息"
              active={tab === 'messages'}
              badge={chatUnread}
              trialEndsAt={chat.trialEndsAt}
              onPress={() => onTabChange('messages')}
            />
          </>
        ) : null}
        <View
          css={`
            margin-left: auto;
            flex-direction: row;
            align-items: center;
          `}
        >
          {tab === 'notifications' && onMarkAllFeedRead && feedUnread > 0 ? (
            <TouchableOpacity
              onPress={onMarkAllFeedRead}
              hitSlop={kHitSlop}
              css={`
                flex-direction: row;
                align-items: center;
                margin-left: ${rpx(12)}px;
                padding: ${rpx(4)}px 0;
              `}
            >
              <Typography.Text
                size={rpx(12)}
                color="#3b82f6"
                style={{ fontFamily: FontFamily.NotoSans.Medium }}
              >
                全部已读
              </Typography.Text>
            </TouchableOpacity>
          ) : null}
          {tab === 'messages' && onOpenContacts ? (
            <TouchableOpacity
              onPress={onOpenContacts}
              hitSlop={kHitSlop}
              css={`
                flex-direction: row;
                align-items: center;
                margin-left: ${rpx(12)}px;
                padding: ${rpx(4)}px 0;
              `}
            >
              <Typography.Text
                size={rpx(12)}
                color="#3b82f6"
                style={{ fontFamily: FontFamily.NotoSans.Medium }}
              >
                通讯录
              </Typography.Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  },
);

const TabButton: FC<{
  label: string;
  active: boolean;
  badge?: number;
  trialEndsAt: string | null;
  onPress: () => void;
}> = memo(({ label, active, badge, trialEndsAt, onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
      <View
        css={`
          flex-direction: row;
          align-items: center;
          padding: ${rpx(12)}px ${rpx(8)}px ${rpx(10)}px;
          border-bottom-width: ${rpx(2)}px;
          border-bottom-color: ${active ? '#111' : 'transparent'};
        `}
      >
        <Typography.Text
          size={rpx(16)}
          color={active ? '#111' : '#999'}
          style={{
            fontFamily: active
              ? FontFamily.NotoSans.Bold
              : FontFamily.NotoSans.Medium,
          }}
        >
          {label}
        </Typography.Text>
        <TrialBadge trialEndsAt={trialEndsAt} variant="onLight" />
        {badge && badge > 0 ? (
          <View
            css={`
              margin-left: ${rpx(6)}px;
            `}
          >
            <UnreadBadge count={badge} />
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
});
